import {
	HttpClient,
	HttpResponse
}                             from '@angular/common/http';
import { Injectable }         from '@angular/core';
import { AbstractApiService } from '@app/core/http/abstract-api.service';
import { SanitizerService }   from '@app/core/http/sanitizer.service';
import { TransitionService }  from '@app/core/http/transition.service';
import {
	IErsHttpParams,
	RequestActionEnum,
	RequestCompleteEntityState,
	RequestCompleteListState
}                             from '@app/core/interfaces/api.interface';
import { ModelTypeEnum }      from '@app/core/models/transition.model';
import {
	Ers,
	ErsStatusEnum
}                             from '@app/modules/ers/models/ers.model';
import { LoggerService }      from '@app/shared/services/logger.service';
import { MapperService }      from '@http/mapper.service';
import { IHttpContextConfig } from '@interceptors/interceptors.model';
import {
	CreateErsRequestParams,
	DeleteErsRequestParams,
	ErsApiClient,
	ErsBodyDTO,
	ErsDeviceTypeEnumDTO,
	ErsDTO,
	ErsStatusDTO,
	GetAllErsRequestParams,
	GetErsByBusinessPremisesRequestParams,
	GetErsByTssRequestParams,
	GetErsRequestParams,
	InvalidEntitiesDTO,
	UpdateErsRequestParams,
	UpdateErsStatusRequestParams
}                             from '@openApiGenerated/management-api';
import { Observable }         from 'rxjs';

@Injectable( {
	providedIn: 'root'
} )
export class ErsApiService extends AbstractApiService {

	constructor(
		private readonly _ersApiClient: ErsApiClient,
		private readonly _mapperService: MapperService,
		private readonly _sanitize: SanitizerService,
		private readonly _transition: TransitionService,
		protected http: HttpClient,
		protected logger: LoggerService
	) {
		super();
		this.http = http;
	}

	private sanitizeApiResponse( entity: Ers, context = this ): void {
		if ( entity instanceof Ers ) {
			entity.acquisitionDate = context._sanitize.date<Date>( entity.acquisitionDate, ModelTypeEnum.TMO, Date );
			entity.activationDate = context._sanitize.date<Date>( entity.activationDate, ModelTypeEnum.TMO, Date );
			entity.commissioningDate = context._sanitize.date<Date>( entity.commissioningDate, ModelTypeEnum.TMO, Date );
			entity.createdDate = context._sanitize.date<Date>( entity.createdDate, ModelTypeEnum.TMO, Date );
			entity.deactivationDate = context._sanitize.date<Date>( entity.deactivationDate, ModelTypeEnum.TMO, Date );
			entity.decommissionDate = context._sanitize.date<Date>( entity.decommissionDate, ModelTypeEnum.TMO, Date );
			entity.deviceType = context._mapperService.ers.deviceTypeDtoToTmo( entity?.deviceType as unknown as ErsDeviceTypeEnumDTO );
			entity.firstConnectionEstablishedDate = context._sanitize.date<Date>( entity.firstConnectionEstablishedDate, ModelTypeEnum.TMO, Date );
			entity.lastModifiedDate = context._sanitize.date<Date>( entity.lastModifiedDate, ModelTypeEnum.TMO, Date );

			if ( entity.errors?.invalidEntities ) {
				entity.errors.invalidEntities =
					context._mapperService.errors.invalidEntitiesDtoToTmo( entity.errors.invalidEntities as unknown as InvalidEntitiesDTO[] );
			}
		}
	}


	// LIST TYPE REQUESTS #############################
	public list(
		options?: {
			httpContextConfig: IHttpContextConfig,
			throwError?: boolean
		}
	): Observable<RequestCompleteListState<Ers>> {
		const {
			httpContextConfig,
			throwError = false
		} = options;

		this.logger.debug( 'pre-list call' );

		const params: IErsHttpParams = {
			sort: 'lastModifiedDate,desc'
		};

		return this.search( { httpContextConfig, params, throwError } );
	}

	public search(
		options: {
			httpContextConfig?: IHttpContextConfig,
			params: IErsHttpParams,
			throwError?: boolean
		}
	): Observable<RequestCompleteListState<Ers>> {
		const {
			httpContextConfig,
			params,
			throwError = false
		} = options;

		this.logger.debug( 'pre-search call: ', params );

		const requestParam: GetAllErsRequestParams = {
			ersIds: this.arrayUtils.checkForSingleValueArgAndReturnAsArray<string>( params?.ersIds ),
			ersSerialNumber: params?.ersSerialNumber,
			filter: params?.filter,
			hasValidationResults: params?.hasValidationResults,
			pageNumber: params?.paging?.pageNumber,
			pageSize: params?.paging?.pageSize,
			search: this.arrayUtils.checkForSingleValueArgAndReturnAsArray<string>( params?.search ),
			sort: params?.sort,
			status: this._mapperService.ers.statusListTmoToDto(
				this.arrayUtils.checkForSingleValueArgAndReturnAsArray( params?.status as unknown as ErsStatusEnum )
			),
		};

		const req$ = this._ersApiClient.getAllErs(
			requestParam,
			'body',
			false,
			this.buildHttpContext( httpContextConfig )
		);

		return this._transition.handleListRequestResponse<ErsDTO, Ers>(
			req$,
			Ers,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			throwError
		);
	}

	public listByBusinessPremisesId(
		options: {
			businessPremisesId: string
			httpContextConfig?: IHttpContextConfig,
			params?: IErsHttpParams,
			throwError?: boolean
		}
	): Observable<RequestCompleteListState<Ers>> {
		const {
			businessPremisesId,
			httpContextConfig,
			params,
			throwError = false
		} = options;

		this.logger.debug( 'pre-listByBusinessPremisesId call: ', businessPremisesId );

		const requestParam: GetErsByBusinessPremisesRequestParams = {
			businessPremisesId,
			ersSerialNumber: params?.ersSerialNumber,
			filter: params?.filter,
			hasValidationResults: params?.hasValidationResults,
			pageNumber: params?.paging?.pageNumber,
			pageSize: params?.paging?.pageSize,
			search: this.arrayUtils.checkForSingleValueArgAndReturnAsArray<string>( params?.search ),
			sort: params?.sort,
			status: this._mapperService.ers.statusListTmoToDto(
				this.arrayUtils.checkForSingleValueArgAndReturnAsArray( params?.status as unknown as ErsStatusEnum )
			),
		};

		const req$ = this._ersApiClient.getErsByBusinessPremises(
			requestParam,
			'body',
			false,
			this.buildHttpContext( httpContextConfig )
		);

		return this._transition.handleListRequestResponse<ErsDTO, Ers>(
			req$,
			Ers,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			throwError
		);
	}

	public listByTssId(
		options: {
			httpContextConfig?: IHttpContextConfig,
			params?: IErsHttpParams,
			throwError?: boolean,
			tssId: string,
		}
	): Observable<RequestCompleteListState<Ers>> {
		const {
			httpContextConfig,
			params,
			throwError = false,
			tssId
		} = options;

		this.logger.debug( 'pre-listByTssId call: ', tssId );

		const requestParam: GetErsByTssRequestParams = {
			ersSerialNumber: params?.ersSerialNumber,
			hasValidationResults: params?.hasValidationResults,
			pageNumber: params?.paging?.pageNumber,
			pageSize: params?.paging?.pageSize,
			search: this.arrayUtils.checkForSingleValueArgAndReturnAsArray<string>( params?.search ),
			sort: params?.sort,
			status: this._mapperService.ers.statusListTmoToDto(
				this.arrayUtils.checkForSingleValueArgAndReturnAsArray( params?.status as unknown as ErsStatusEnum )
			),
			tssId
		};

		const req$ = this._ersApiClient.getErsByTss(
			requestParam,
			'body',
			false,
			this.buildHttpContext( httpContextConfig )
		);

		return this._transition.handleListRequestResponse<ErsDTO, Ers>(
			req$,
			Ers,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			throwError
		);
	}


	// ENTITY TYPE REQUESTS ###########################
	public read(
		options: {
			ersId: string,
			httpContextConfig?: IHttpContextConfig,
			throwError?: boolean,
			withFullResponse?: boolean
		}
	): Observable<RequestCompleteEntityState<Ers>> {
		const {
			ersId,
			httpContextConfig,
			throwError = false,
			withFullResponse = false
		} = options;

		this.logger.debug( 'pre-read call: ', `id: ${ ersId }`, `withFullResponse: ${ withFullResponse }` );
		const requestParam: GetErsRequestParams = {
			ersId
		};

		const req$ = this._ersApiClient.getErs(
			requestParam,
			withFullResponse ? 'response' : undefined,
			false,
			this.buildHttpContext( httpContextConfig )
		);

		return this._transition.handleEntityRequestResponse<ErsDTO | HttpResponse<ErsDTO>, Ers>(
			req$,
			RequestActionEnum.READ,
			Ers,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}

	public create(
		options: {
			ers: Ers,
			httpContextConfig?: IHttpContextConfig,
			throwError?: boolean
		}
	): Observable<RequestCompleteEntityState<Ers>> {
		const {
			ers,
			httpContextConfig,
			throwError = false
		} = options;

		this.logger.debug( 'pre-create call: ', { ...ers } );

		const ersBodyDTO: ErsBodyDTO = this._mapperService.ers.ersToErsBodyDTO( ers, this._sanitize );
		const requestParam: CreateErsRequestParams = {
			businessPremisesId: ers.businessPremisesId,
			ersBodyDTO
		};

		const req$ = this._ersApiClient.createErs(
			requestParam,
			'body',
			false,
			this.buildHttpContext( httpContextConfig )
		);

		return this._transition.handleEntityRequestResponse<ErsDTO, Ers>(
			req$,
			RequestActionEnum.CREATE,
			Ers,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}

	public update(
		options: {
			ers: Ers,
			httpContextConfig?: IHttpContextConfig,
			throwError?: boolean,
			withFullResponse?: boolean
		}
	): Observable<RequestCompleteEntityState<Ers>> {
		const {
			ers,
			httpContextConfig,
			throwError = false,
			withFullResponse = false
		} = options;

		this.logger.debug( 'pre-update call: ', { ...ers } );

		const ersBodyDTO: ErsBodyDTO = this._mapperService.ers.ersToErsBodyDTO( ers, this._sanitize );
		const requestParam: UpdateErsRequestParams = {
			ersId: ers.id,
			ifMatch: ers.etag,
			ersBodyDTO
		};

		const req$ = this._ersApiClient.updateErs(
			requestParam,
			withFullResponse ? 'response' : undefined,
			false,
			this.buildHttpContext( httpContextConfig )
		);

		return this._transition.handleEntityRequestResponse<ErsDTO | HttpResponse<ErsDTO>, Ers>(
			req$,
			RequestActionEnum.UPDATE,
			Ers,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}

	public updateStatus(
		options: {
			ers: Ers,
			httpContextConfig?: IHttpContextConfig,
			throwError?: boolean,
			withFullResponse?: boolean
		}
	): Observable<RequestCompleteEntityState<Ers>> {
		const {
			ers,
			httpContextConfig,
			throwError = false,
			withFullResponse = false
		} = options;

		this.logger.debug( 'pre-update call: ', { ...ers } );

		const ersStatusDTO: ErsStatusDTO = {
			status: this._mapperService.ers.statusTmoToDto( ers.status )
		};

		const requestParam: UpdateErsStatusRequestParams = {
			ersId: ers.id,
			ifMatch: ers.etag,
			ersStatusDTO
		};

		const req$ = this._ersApiClient.updateErsStatus(
			requestParam,
			withFullResponse ? 'response' : undefined,
			false,
			this.buildHttpContext( httpContextConfig )
		);

		return this._transition.handleEntityRequestResponse<ErsDTO | HttpResponse<ErsDTO>, Ers>(
			req$,
			RequestActionEnum.UPDATE,
			Ers,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}

	public delete(
		options: {
			ersId: string,
			httpContextConfig?: IHttpContextConfig,
			throwError?: boolean
		}
	): Observable<RequestCompleteEntityState<Ers>> {
		const {
			ersId,
			httpContextConfig,
			throwError = false
		} = options;

		this.logger.debug( 'pre-delete call: ', ersId );
		const requestParam: DeleteErsRequestParams = {
			ersId
		};

		const req$ = this._ersApiClient.deleteErs(
			requestParam,
			'response',
			false,
			this.buildHttpContext( httpContextConfig )
		);

		return this._transition.handleEntityRequestResponse<HttpResponse<any>, Ers>(
			req$,
			RequestActionEnum.DELETE,
			Ers,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}
}
